import {hoursToSeconds} from '@wandb/weave/common/util/time';
import {isNotNullOrUndefined} from '@wandb/weave/common/util/types';
// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {Dispatch, SetStateAction, useContext, useState} from 'react';
import {useHistory} from 'react-router-dom';

import {AccountSelectorContext} from '../../../../components/Search/SearchNav/AccountSelectorContextProvider';
import {
  AccountPlanType,
  AccountType,
} from '../../../../components/Search/SearchNav/types';
import {
  CurrentBillingPeriodUsageSectionOrgInfoQuery,
  OrganizationSubscriptionType,
  StorageUnit,
  UsageType,
  useCurrentBillingPeriodUsageSectionOrgInfoQuery,
  useEnterpriseBillingPeriodOrgInfoQuery,
  useUsageTabInfoQuery,
  useUsageTotalInfoQuery,
} from '../../../../generated/graphql';
import {TRIAL_END_PATH} from '../../../../routes/paths';
import {
  getPrimarySub,
  getSeatsSub,
  getStorageSub,
  getTrackedHoursSub,
  getTrackedHourUsageSub,
  getWeaveOverageSubBillingPeriod,
  HasSubsWithPlanName,
  HasSubsWithPlanType,
  HasSubsWithSubType,
  isAcademicSubscription,
  isCommunityEditionPrimarySubscription,
  isProPrimarySubscription,
  isTrialPlanPrimarySubscription,
  Privileges,
  StripeElementsComp,
  SubscriptionForOrg,
  useAccountPrivileges,
  useAccountWeaveLimit,
} from '../../../../util/accounts/pricing';
import {
  addDaysUTC,
  addMonthsUTC,
  differenceInCalendarDaysUTC,
  isAfter,
  secondsToHours,
  startOfMonthUTC,
} from '../../../../util/date';
import {
  useRampFlagInferenceCatalog,
  useRampInferenceAsProduct,
} from '../../../../util/rampFeatureFlags';
import {
  bytesInGB,
  bytesInKB,
  bytesInMB,
  bytesInPB,
  bytesInTB,
  freePersonalStorageInGB,
  getEstimatedStorageCharge,
  getFreeStorageInGB,
} from '../../../../util/storage';
import {
  accountSettingsMembersTab,
  accountSettingsUsagePage,
  contactSalesPricing,
  historicUsageTab,
} from '../../../../util/urls';
import {useGetInferenceUsageDetails} from '../../../../util/usage/inference';
import {getNormalizedUsageTotal} from '../../../../util/usage/utils';
import {TABS} from '../../../HistoricUsage/HistoricUsage';
import {valueAggregator} from '../../../HistoricUsage/HistoricUsageChartContextProvider';
import {
  DEFAULT_DAYS_AGO,
  DEFAULT_INTERVAL_MARKERS,
  DEFAULT_NUMBER_OF_DAYS,
  getDateRangeText,
  MeterSection,
  Props as MeterSectionProps,
  TODAY,
  TOMORROW,
} from '../../../HistoricUsage/Meter/MeterSection';
import {MeterSectionError} from '../../../HistoricUsage/Meter/MeterSectionError';
import {MeterSectionLoading} from '../../../HistoricUsage/Meter/MeterSectionLoading';
import {CustomizePlanModal} from '../../Checkout/CustomizePlanModal';
import {NewStorageCheckoutModal} from '../../Checkout/NewStorageCheckoutModal';
import {PlanName} from '../../util';
import {canSeeTrialEndPage} from '../WarningBanner/ExpiryBannerContent';
import {
  getFormattedBillingDate,
  METER_STYLE,
  UpgradePlanLink,
} from './PlanCard/PlanCardComponents';
export function formatOverageChargeText(cost: number): string {
  return `Overage charge: ${new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cost)}`;
}

export const ZERO_COST_TEXT = formatOverageChargeText(0);

type Organization = NonNullable<
  CurrentBillingPeriodUsageSectionOrgInfoQuery['organization']
>;
type Subscription = Pick<
  Organization['subscriptions'][number],
  'billingPeriodStart' | 'billingPeriodEnd'
>;
const START_OF_CURRENT_CALENDAR_MONTH = startOfMonthUTC(TODAY);
const START_OF_NEXT_CALENDAR_MONTH = startOfMonthUTC(addMonthsUTC(TODAY, 1));
const CURRENT_CALENDAR_MONTH_INTERVAL_MARKERS = [
  START_OF_CURRENT_CALENDAR_MONTH,
  START_OF_NEXT_CALENDAR_MONTH,
];

export function getIntervalMarkers(
  subscription: Subscription | null | undefined,
  usageType: UsageType
): Date[] {
  if (
    subscription?.billingPeriodStart == null ||
    subscription?.billingPeriodEnd == null
  ) {
    if (
      usageType === UsageType.Weave ||
      usageType === UsageType.InferenceCost
    ) {
      return CURRENT_CALENDAR_MONTH_INTERVAL_MARKERS;
    }
    return DEFAULT_INTERVAL_MARKERS;
  }
  const billingPeriodStart = new Date(subscription?.billingPeriodStart);
  const billingPeriodEnd = new Date(subscription?.billingPeriodEnd);
  if (usageType === UsageType.Storage) {
    // Since storage is an average value, we will add in the current value separately in case it hasn't been aggregated yet
    return [billingPeriodStart, TODAY];
  }
  return [billingPeriodStart, billingPeriodEnd];
}

export function useGetUsageThisBillingPeriod(
  orgName: string,
  orgCreatedAt: Date | undefined,
  usageType: UsageType,
  subscription: Subscription | null | undefined,
  // not supported yet but we need to for storage
  currentValue: number, // we need to add today's real-time value to the total since it hasn't been aggregated yet
  skip: boolean
) {
  const intervalMarkers = getIntervalMarkers(subscription, usageType);
  const {data, loading, error} = useUsageTotalInfoQuery({
    variables: {
      orgName,
      usageType,
      intervalMarkers: intervalMarkers.map(marker => marker.toISOString()),
    },
    skip: skip || isAfter(intervalMarkers[0], intervalMarkers[1]), // skip if the interval markers are out of order (i.e. the subscription started today)
  });
  const [intervalStart, intervalEnd] = intervalMarkers;
  const [usage] = getNormalizedUsageTotal(data?.organization, usageType) || [0];

  return {
    usage,
    billingPeriodEnd: intervalEnd,
    billingPeriodStart: intervalStart,
    loading,
    error,
  };
}

/**
 * @deprecated use `useGetUsageThisBillingPeriod` instead.
 */
export function useLegacyGetUsageTypeUsageThisBillingPeriod(
  orgName: string,
  orgCreatedAt: Date | undefined,
  usageType: UsageType,
  subscription: Subscription | null | undefined,
  currentValue: number, // we need to add today's real-time value to the total since it hasn't been aggregated yet
  skip: boolean
) {
  const intervalMarkers = getIntervalMarkers(subscription, usageType);
  const {data, loading, error} = useUsageTabInfoQuery({
    variables: {
      orgName,
      usageType,
      intervalMarkers: intervalMarkers.map(marker => marker.toISOString()),
    },
    skip: skip || isAfter(intervalMarkers[0], intervalMarkers[1]), // skip if the interval markers are out of order (i.e. the subscription started today)
  });
  const [intervalStart, intervalEnd] = getIntervalMarkers(
    subscription,
    usageType
  );
  intervalStart.setUTCHours(0, 0, 0, 0);
  const initialUsageData = {
    start: intervalStart.toISOString(),
    end: TODAY.toISOString(),
    value: currentValue,
  };
  const usageData = [
    initialUsageData,
    ...(data?.organization?.usage?.map(dataRow => ({
      ...dataRow,
      end: TODAY.toISOString(),
    })) ?? []),
  ]; // valueAggregator uses the `start` and `end` to calculate what to divide by for the interval - but since we added the current day's data, we should update it so the denominator's right
  const usage = usageData.reduce(
    (total, {value, start, end}) =>
      valueAggregator(
        {
          value,
          start,
          end,
        },
        usageType,
        total,
        orgCreatedAt
      ),
    0
  );
  return {
    usage,
    billingPeriodEnd: intervalEnd,
    billingPeriodStart: intervalStart,
    loading,
    error,
  };
}

export const PLAN_USAGE_TITLE = 'Plan usage';

export function getWeaveOverageCostText(
  overageInBytes: number,
  weaveOverageUnit: StorageUnit,
  weaveOverageCostCents: number
) {
  const overageinOverageUnit = convertToOverageUnit(
    overageInBytes,
    weaveOverageUnit
  );
  const costInCents = Math.max(0, overageinOverageUnit * weaveOverageCostCents);
  return formatOverageChargeText(costInCents / 100);
}

export function convertToOverageUnit(
  overageBytes: number,
  weaveOverageUnit: StorageUnit
): number {
  switch (weaveOverageUnit) {
    case StorageUnit.B:
      return overageBytes;
    case StorageUnit.Kb:
      return overageBytes / bytesInKB;
    case StorageUnit.Mb:
      return overageBytes / bytesInMB;
    case StorageUnit.Gb:
      return overageBytes / bytesInGB;
    case StorageUnit.Tb:
      return overageBytes / bytesInTB;
    case StorageUnit.Pb:
      return overageBytes / bytesInPB;
  }
}

export function getStorageLimitGB<
  T extends HasSubsWithPlanType & HasSubsWithPlanName & HasSubsWithSubType,
>(privileges: Privileges, organization: T | undefined): number | undefined {
  const storageLimit = privileges.storageLimitGB;

  const storageSub = getStorageSub(organization);
  const primarySub = getPrimarySub(organization);

  // enterprise plan has unlimited storage
  if (
    primarySub?.plan?.name === PlanName.Enterprise &&
    primarySub?.subscriptionType !== OrganizationSubscriptionType.Academic &&
    primarySub?.subscriptionType !== OrganizationSubscriptionType.AcademicTrial
  ) {
    return undefined;
  }

  // if user is on the storage plan, it's pay as you go
  // so they technically have unlimited limit
  if (storageSub != null) {
    return undefined;
  }

  return storageLimit;
}

type CurrentBillingPeriodUsageProps = {
  meterRows: {
    title?: string;
    subtitle?: string;
    rows: (MeterSectionProps | null)[];
  }[];
  organization?: Organization;
  isProPlan?: boolean;
  isProMonthly?: boolean;
  loading: boolean;
  hasError: boolean;
  onClickViewUsage: ((usageType: UsageType) => void) | null;
  onClickCustomizePlan: Dispatch<SetStateAction<boolean>> | null;
};

export type UsageThisBillingPeriod = Pick<
  CurrentBillingPeriodUsageProps,
  | 'hasError'
  | 'loading'
  | 'meterRows'
  | 'organization'
  | 'isProPlan'
  | 'isProMonthly'
> & {
  privileges?: Privileges;
};

// function to calculate tracked hours cost
export function calculateTrackedHoursCost(
  isProPlan: boolean,
  trackedHoursDataUsage: number,
  billingPeriodTrackedHoursUsage: number,
  privileges: Privileges
) {
  if (!isProPlan) {
    return Math.max(
      0,
      Math.min(
        secondsToHours(trackedHoursDataUsage),
        secondsToHours(billingPeriodTrackedHoursUsage) - privileges.computeHours
      )
    );
  } else {
    return trackedHoursDataUsage >
      hoursToSeconds(privileges.computeHoursMonthlyLimit)
      ? secondsToHours(trackedHoursDataUsage) -
          privileges.computeHoursMonthlyLimit
      : 0;
  }
}

function padArrayEnd<T>(arr: T[], targetLength: number, padValue: T): T[] {
  const padded = arr.slice(); // copy to avoid mutation
  while (padded.length < targetLength) {
    padded.push(padValue);
  }
  return padded;
}

function useGetAllUsageThisBillingPeriod(
  setShowNewStorageCheckoutModal?: (show: boolean) => void
): UsageThisBillingPeriod {
  const {selectedAccount} = useContext(AccountSelectorContext);
  const history = useHistory();
  const skip =
    selectedAccount?.name == null ||
    selectedAccount?.accountType === AccountType.Personal;
  const orgName = selectedAccount?.name ?? '';
  const isEnterprise = selectedAccount?.isEnterprise === true;

  const {
    data: orgInfo,
    loading: orgLoading,
    error: orgError,
  } = useCurrentBillingPeriodUsageSectionOrgInfoQuery({
    variables: {orgName},
    skip,
  });

  // Query this separately because most orgs won't need to query this field.
  const {
    data: enterpriseOrgInfo,
    loading: enterpriseOrgLoading,
    error: enterpriseOrgError,
  } = useEnterpriseBillingPeriodOrgInfoQuery({
    variables: {orgName},
    skip: skip || !isEnterprise,
  });

  const anyOrgLoading = orgLoading || enterpriseOrgLoading;
  const anyOrgError = orgError || enterpriseOrgError;

  const {loading: privilegesLoading, privileges} =
    useAccountPrivileges(selectedAccount);

  const {loading: weaveLimitLoading, weaveLimit} =
    useAccountWeaveLimit(selectedAccount);

  // Merge two org queries together
  const mergedOrg =
    orgInfo?.organization || enterpriseOrgInfo?.organization
      ? {
          ...(orgInfo?.organization ?? {}),
          ...(enterpriseOrgInfo?.organization ?? {}),
        }
      : undefined;

  // Only return organization if it has required properties
  const organization =
    mergedOrg?.id &&
    mergedOrg?.name &&
    mergedOrg?.createdAt &&
    mergedOrg?.teams &&
    mergedOrg?.subscriptions
      ? (mergedOrg as Organization)
      : undefined;

  const orgCreatedAt =
    organization?.createdAt != null
      ? new Date(organization.createdAt)
      : undefined;
  const primarySub = getPrimarySub(organization);
  const isProPlan = primarySub != null && isProPrimarySubscription(primarySub);
  const isEnterprisePlan =
    selectedAccount != null && selectedAccount.isEnterprise;
  const isProMonthly = primarySub?.plan?.name === PlanName.ProMonthly;
  const isFreePlanOrTrialPlan =
    primarySub != null &&
    (isCommunityEditionPrimarySubscription(primarySub) ||
      isTrialPlanPrimarySubscription(primarySub));

  const isAcademicPlan =
    primarySub != null && isAcademicSubscription(primarySub);

  const enterpriseSubscription =
    enterpriseOrgInfo?.organization?.weaveEnterpriseSubscription;

  // Since tracked hours billing periods end at particular times of day and are reported to stripe throughout the day,
  //  we want to not just use the daily aggregation from the usage page, and instead use the computeHours prop on the organization.
  const trackedHoursSub =
    (isProPlan
      ? getTrackedHourUsageSub(organization)
      : getTrackedHoursSub(organization)) || enterpriseSubscription;

  const modelSeatsSub = getSeatsSub(organization);

  const [trackedHoursStart, trackedHoursEnd] = getIntervalMarkers(
    trackedHoursSub,
    UsageType.TrackedHours
  );
  const {
    data: trackedHoursComputeSecondsData,
    loading: trackedHoursLoading,
    error: trackedHoursError,
  } = useUsageTabInfoQuery({
    variables: {
      orgName,
      usageType: UsageType.TrackedHours,
      intervalMarkers: [trackedHoursStart, trackedHoursEnd].map(marker =>
        marker.toISOString()
      ),
    },
    skip: skip || anyOrgLoading || organization?.id == null,
  });

  const billingPeriodTrackedHoursData =
    useLegacyGetUsageTypeUsageThisBillingPeriod(
      orgName,
      orgCreatedAt,
      UsageType.TrackedHours,
      isProPlan ? trackedHoursSub : primarySub,
      0,
      skip || anyOrgLoading || trackedHoursSub == null
    );

  const trackedHoursData = {
    usage:
      trackedHoursComputeSecondsData?.organization?.usage?.reduce(
        (total, {value}) => total + value,
        0
      ) ?? 0,
    billingPeriodEnd:
      trackedHoursSub?.billingPeriodEnd != null
        ? new Date(trackedHoursSub.billingPeriodEnd)
        : undefined,
    billingPeriodStart:
      trackedHoursSub?.billingPeriodStart != null
        ? new Date(trackedHoursSub.billingPeriodStart)
        : undefined,
    loading: trackedHoursLoading,
    error: trackedHoursError,
  };

  const trackedHoursCost = calculateTrackedHoursCost(
    isProPlan,
    trackedHoursData.usage,
    billingPeriodTrackedHoursData.usage,
    privileges
  );
  const trackedHoursCostText = formatOverageChargeText(trackedHoursCost);

  const storageSub = getStorageSub(organization) || enterpriseSubscription;
  const currentStorageBytes =
    organization?.teams?.reduce(
      (total, {storageBytes}) => total + storageBytes,
      0
    ) ?? 0;
  const storageData = useLegacyGetUsageTypeUsageThisBillingPeriod(
    orgName,
    orgCreatedAt,
    UsageType.Storage,
    storageSub,
    currentStorageBytes,
    skip || anyOrgLoading
  );

  if (isFreePlanOrTrialPlan) {
    // orgs on Free or Trial plan don't need to know average storage usage.
    // the lock out state depends on the current storage usage
    storageData.usage = currentStorageBytes;
  }

  const storageLimitGB = getStorageLimitGB(privileges, organization);
  const numDaysLeftInBillingPeriod =
    storageSub?.billingPeriodEnd != null
      ? differenceInCalendarDaysUTC(
          new Date(storageSub.billingPeriodEnd),
          TODAY
        ) + 1
      : null;
  // Do a weighted average assuming the user won't touch their storage over the rest of their
  // billing period.
  const estimatedAverageStorageBytes =
    numDaysLeftInBillingPeriod != null
      ? (storageData.usage *
          (DEFAULT_NUMBER_OF_DAYS - numDaysLeftInBillingPeriod) +
          currentStorageBytes * numDaysLeftInBillingPeriod) /
        DEFAULT_NUMBER_OF_DAYS
      : storageData.usage;
  const storageLimitBytes =
    selectedAccount?.isEnterprise === true
      ? undefined
      : (storageLimitGB ??
          (primarySub
            ? getFreeStorageInGB(primarySub, privileges)
            : freePersonalStorageInGB)) * bytesInGB;
  const storageCost =
    storageLimitBytes != null
      ? getEstimatedStorageCharge(
          estimatedAverageStorageBytes,
          primarySub as SubscriptionForOrg<HasSubsWithSubType>,
          privileges
        )
      : 0;
  const storageCostText =
    storageCost === 0 ? (
      formatOverageChargeText(storageCost)
    ) : storageSub != null ? (
      formatOverageChargeText(storageCost)
    ) : (
      <>
        Over limit. <UpgradePlanLink /> for more storage
      </>
    );

  const weaveLimitBytes = weaveLimit?.weaveLimitBytes;
  const weaveOverageCostCents = weaveLimit?.weaveOverageCostCents;
  const weaveOverageUnit = weaveLimit?.weaveOverageUnit;

  const weaveOverageData = useLegacyGetUsageTypeUsageThisBillingPeriod(
    orgName,
    orgCreatedAt,
    UsageType.Weave,
    getWeaveOverageSubBillingPeriod(privileges, organization),
    0,
    skip || anyOrgLoading
  );
  const weaveFraction =
    weaveLimitBytes != null ? weaveOverageData.usage / weaveLimitBytes : 0;
  const showUpgradeWeave =
    selectedAccount?.isEnterprise === true ||
    (primarySub != null &&
      weaveFraction >= 1 &&
      canSeeTrialEndPage(primarySub)); // only show upgrade if the usage is over their limit

  let weaveCostTextThisMonth = ZERO_COST_TEXT;
  let inferenceCostTextThisMonth = '';
  const isEnterpriseWithOrbSubscription =
    selectedAccount?.isEnterprise && enterpriseSubscription != null;
  if (
    (isProPlan || isEnterpriseWithOrbSubscription) &&
    weaveLimitBytes != null &&
    weaveOverageCostCents != null &&
    weaveOverageUnit != null &&
    weaveOverageCostCents !== 0
  ) {
    weaveCostTextThisMonth = getWeaveOverageCostText(
      weaveOverageData.usage - weaveLimitBytes, // overage this month
      weaveOverageUnit,
      weaveOverageCostCents
    );
  } else {
    weaveCostTextThisMonth = '';
  }

  const {limits: inferenceLimits, ...inferenceUsageResult} =
    useGetInferenceUsageDetails(selectedAccount);

  const inferenceFraction =
    inferenceUsageResult.usage / inferenceLimits.inferenceFreeLimit;

  if (inferenceUsageResult.isUsagePaid) {
    inferenceCostTextThisMonth = ZERO_COST_TEXT;
    if (inferenceUsageResult.usage > inferenceLimits.inferenceFreeLimit) {
      inferenceCostTextThisMonth = formatOverageChargeText(
        (inferenceUsageResult.usage - inferenceLimits.inferenceFreeLimit) / 100
      );
    }
  }

  const showUpgradeInference =
    selectedAccount?.isEnterprise === true ||
    (primarySub != null &&
      inferenceFraction >= 1 &&
      canSeeTrialEndPage(primarySub)); // only show upgrade if the usage is over their limit

  const showPurchaseInferenceAsProduct =
    useRampInferenceAsProduct() &&
    (isFreePlanOrTrialPlan || isAcademicPlan) &&
    !inferenceUsageResult.isUsagePaid &&
    !inferenceUsageResult.loading;

  const isEnterpriseWithoutOrbSubscription =
    selectedAccount?.isEnterprise && enterpriseSubscription == null;
  const isEnterprisePlanPastWeaveTrialExpiration =
    isEnterpriseWithoutOrbSubscription &&
    isAfter(TODAY, privileges.weaveLimitDateInterval.end);
  // If all billing periods are the same, we can hide the billing period start and end dates on the meters
  // and just show the range at the top.
  const billingPeriods = [
    trackedHoursData,
    storageData,
    isEnterprisePlanPastWeaveTrialExpiration ? null : weaveOverageData,
    inferenceUsageResult,
  ].filter(isNotNullOrUndefined);
  const areAllBillingPeriodsTheSame = billingPeriods.every(
    ({billingPeriodStart, billingPeriodEnd}, i) =>
      i === 0 ||
      (billingPeriodStart === billingPeriods[i - 1].billingPeriodStart &&
        billingPeriodEnd === billingPeriods[i - 1].billingPeriodEnd)
  );
  const overallBillingPeriod =
    areAllBillingPeriodsTheSame &&
    billingPeriods[0].billingPeriodStart != null &&
    billingPeriods[0].billingPeriodEnd != null
      ? {
          start: billingPeriods[0].billingPeriodStart,
          end: billingPeriods[0].billingPeriodEnd,
        }
      : {start: DEFAULT_DAYS_AGO, end: TOMORROW};
  const isWeaveTrial =
    isEnterpriseWithoutOrbSubscription &&
    !isEnterprisePlanPastWeaveTrialExpiration;

  // TODO(hhuang): Update when we have billing non trial stuff
  const isInferenceTrial =
    selectedAccount?.isEnterprise === true && !inferenceUsageResult.isUsagePaid;
  const isEnterprisePlanPastInferenceTrialExpiration = isAfter(
    TODAY,
    inferenceUsageResult.billingPeriodEnd
  );
  const isInferenceRampEnabled = useRampFlagInferenceCatalog(
    selectedAccount?.name ?? ''
  );
  const hideInference =
    isEnterprisePlanPastInferenceTrialExpiration ||
    !isInferenceRampEnabled ||
    selectedAccount?.accountPlanType === AccountPlanType.Personal ||
    selectedAccount?.accountPlanType === AccountPlanType.Teams;
  const trialProps: Array<MeterSectionProps | null> = [];
  const regularProps: Array<MeterSectionProps | null> = [];

  const weaveProps: MeterSectionProps | null =
    isEnterprisePlanPastWeaveTrialExpiration
      ? null
      : {
          value: weaveOverageData.usage,
          title: 'Weave data ingestion',
          usageType: UsageType.Weave,
          isTrial: isWeaveTrial ?? false,
          billingPeriodEnd: weaveOverageData.billingPeriodEnd,
          billingPeriodStart: weaveOverageData.billingPeriodStart,
          limit: weaveLimit?.weaveLimitBytes,
          isUnlimited: weaveLimit?.weaveLimitBytes == null,
          costText: weaveCostTextThisMonth,
          hideDateInterval: isProPlan,
          customDateInterval: isWeaveTrial
            ? `Trial expires ${getFormattedBillingDate(
                addDaysUTC(privileges.weaveLimitDateInterval.end, 1)
              )} UTC`
            : undefined,
          button: showUpgradeWeave ? (
            <Button
              variant={weaveFraction >= 1 ? 'primary' : 'secondary'}
              onClick={() =>
                selectedAccount?.isEnterprise === true
                  ? // This is an external URL, so we need it to be unprefixed
                    // eslint-disable-next-line wandb/no-unprefixed-urls
                    window.open(contactSalesPricing())
                  : history.push(TRIAL_END_PATH)
              }>
              {selectedAccount?.isEnterprise === true
                ? 'Contact sales'
                : 'Upgrade plan'}
            </Button>
          ) : null,
        };

  const inferenceProps: MeterSectionProps | null = hideInference
    ? null
    : {
        value: inferenceUsageResult.usage,
        title: 'W&B Inference',
        isTrial: isInferenceTrial,
        usageType: UsageType.InferenceCost,
        billingPeriodEnd: inferenceUsageResult.billingPeriodEnd,
        billingPeriodStart: inferenceUsageResult.billingPeriodStart,
        limit: inferenceLimits.inferenceFreeLimit,
        isUnlimited: !inferenceLimits.enforceInferenceFreeLimit,
        costText: inferenceCostTextThisMonth,
        hideDateInterval: isProPlan,
        customDateInterval: isInferenceTrial
          ? `Trial expires ${getFormattedBillingDate(
              addDaysUTC(inferenceUsageResult.billingPeriodEnd, 1)
            )} UTC`
          : undefined,
        button: showPurchaseInferenceAsProduct ? (
          <Button
            variant="primary"
            onClick={() => setShowNewStorageCheckoutModal?.(true)}>
            Activate pay-as-you-go
          </Button>
        ) : showUpgradeInference ? (
          <Button
            variant={inferenceFraction >= 1 ? 'primary' : 'secondary'}
            onClick={() =>
              selectedAccount?.isEnterprise === true
                ? // This is an external URL, so we need it to be unprefixed
                  // eslint-disable-next-line wandb/no-unprefixed-urls
                  window.open(contactSalesPricing())
                : history.push(TRIAL_END_PATH)
            }>
            {selectedAccount?.isEnterprise === true
              ? 'Contact sales'
              : 'Upgrade plan'}
          </Button>
        ) : null,
      };

  if (isWeaveTrial) {
    trialProps.push(weaveProps);
  } else {
    regularProps.push(weaveProps);
  }
  if (isInferenceTrial) {
    trialProps.push(inferenceProps);
  } else {
    regularProps.push(inferenceProps);
  }

  const seatsProps: MeterSectionProps = {
    value: organization?.usedSeats ?? 0,
    title: 'Models seats',
    usageType: UsageType.UserSeats,
    limit: modelSeatsSub?.seats,
    isUnlimited: false,
    hideDateInterval: true,
    isTrial: false,
  };

  const trackedHoursRowProps: MeterSectionProps = {
    value: trackedHoursData.usage,
    title: 'Models tracked hours',
    usageType: UsageType.TrackedHours,
    billingPeriodEnd: trackedHoursData.billingPeriodEnd,
    billingPeriodStart: trackedHoursData.billingPeriodStart,
    limit: isProPlan
      ? hoursToSeconds(privileges.computeHoursMonthlyLimit)
      : undefined,
    isUnlimited: !isProPlan,
    costText: trackedHoursCostText,
    hideDateInterval: areAllBillingPeriodsTheSame || isProPlan,
    isTrial: false,
  };

  const storageRowProps: MeterSectionProps = {
    value: storageData.usage,
    title: 'Models storage',
    usageType: UsageType.Storage,
    billingPeriodEnd: storageData.billingPeriodEnd,
    billingPeriodStart: storageData.billingPeriodStart,
    limit: storageLimitBytes,
    isUnlimited: storageLimitGB == null,
    costText: storageCostText,
    button: (
      <Button
        variant="secondary"
        onClick={() => history.push(accountSettingsUsagePage(orgName))}>
        Manage storage
      </Button>
    ),
    hideDateInterval: areAllBillingPeriodsTheSame,
    isTrial: false,
  };

  let meterRows: Array<{
    title?: string;
    subtitle?: string;
    rows: Array<MeterSectionProps | null>;
  }> = [
    {
      title: PLAN_USAGE_TITLE,
      subtitle: areAllBillingPeriodsTheSame
        ? `${getDateRangeText(
            overallBillingPeriod.start,
            overallBillingPeriod.end
          )} UTC`
        : undefined,
      rows: [trackedHoursRowProps, storageRowProps].filter(
        isNotNullOrUndefined
      ),
    },
  ];
  const filteredTrialProps = trialProps.filter(isNotNullOrUndefined);
  const filteredRegularProps = regularProps.filter(isNotNullOrUndefined);

  if (filteredRegularProps.length > 0) {
    meterRows.push({
      rows: padArrayEnd(filteredRegularProps, 2, null),
    });
  }
  if (filteredTrialProps.length > 0) {
    const firstProp = filteredTrialProps[0];
    const allSamePeriod = filteredTrialProps.every(props => {
      return (
        props.billingPeriodStart?.getTime() ===
          firstProp.billingPeriodStart?.getTime() &&
        props.billingPeriodEnd?.getTime() ===
          firstProp.billingPeriodEnd?.getTime()
      );
    });
    meterRows.push({
      title: 'Trials',
      subtitle:
        allSamePeriod &&
        firstProp.billingPeriodStart != null &&
        firstProp.billingPeriodEnd != null
          ? `${getDateRangeText(
              firstProp.billingPeriodStart,
              firstProp.billingPeriodEnd
            )} UTC`
          : undefined,
      rows: padArrayEnd(filteredTrialProps, 2, null),
    });
  }

  if (isProPlan || isFreePlanOrTrialPlan) {
    meterRows = [
      {
        title: 'Usage this billing period',
        subtitle: `${getDateRangeText(
          trackedHoursData.billingPeriodStart ?? DEFAULT_DAYS_AGO,
          trackedHoursData.billingPeriodEnd ?? TOMORROW
        )} UTC`,
        rows: [seatsProps, weaveProps].filter(isNotNullOrUndefined),
      },
      {
        rows: padArrayEnd([trackedHoursRowProps, inferenceProps], 2, null),
      },
      {
        rows: padArrayEnd([storageRowProps], 2, null),
      },
    ];
  }

  if (isEnterprisePlan) {
    meterRows = [
      {
        title: 'Usage',
        rows: [seatsProps, trackedHoursRowProps].filter(isNotNullOrUndefined),
      },
      {
        rows: padArrayEnd([storageRowProps], 2, null),
      },
    ];

    const filteredTrialProps = trialProps.filter(isNotNullOrUndefined);
    const filteredRegularProps = regularProps.filter(isNotNullOrUndefined);

    if (filteredRegularProps.length > 0) {
      meterRows.push({
        rows: padArrayEnd(filteredRegularProps, 2, null),
      });
    }
    if (filteredTrialProps.length > 0) {
      const firstProp = filteredTrialProps[0];
      const allSamePeriod = filteredTrialProps.every(props => {
        return (
          props.billingPeriodStart?.getTime() ===
            firstProp.billingPeriodStart?.getTime() &&
          props.billingPeriodEnd?.getTime() ===
            firstProp.billingPeriodEnd?.getTime()
        );
      });
      meterRows.push({
        title: 'Trials',
        subtitle:
          allSamePeriod &&
          firstProp.billingPeriodStart != null &&
          firstProp.billingPeriodEnd != null
            ? `${getDateRangeText(
                firstProp.billingPeriodStart,
                firstProp.billingPeriodEnd
              )} UTC`
            : undefined,
        rows: padArrayEnd(filteredTrialProps, 2, null),
      });
    }
  }

  return {
    meterRows,
    organization,
    isProPlan,
    isProMonthly,
    loading:
      anyOrgLoading ||
      trackedHoursData.loading ||
      billingPeriodTrackedHoursData.loading ||
      weaveOverageData.loading ||
      storageData.loading ||
      inferenceUsageResult.loading ||
      privilegesLoading,
    hasError:
      anyOrgError != null ||
      trackedHoursData.error != null ||
      billingPeriodTrackedHoursData.error != null ||
      weaveOverageData.error != null ||
      inferenceUsageResult.error != null ||
      storageData.error != null,
    privileges,
  };
}

export const CurrentBillingPeriodUsageSectionInner = ({
  meterRows,
  organization,
  isProPlan,
  isProMonthly,
  onClickCustomizePlan,
  hasError,
  loading,
  onClickViewUsage,
}: CurrentBillingPeriodUsageProps) => {
  const history = useHistory();
  return (
    <Tailwind>
      <div className="flex flex-col">
        {meterRows.map(({title, subtitle, rows}, row) => (
          <div className="flex flex-col" key={row}>
            <div className="mb-8 flex flex-col">
              {title != null && (
                <div className="font-semibold text-moon-800">{title}</div>
              )}
              {subtitle != null && (
                <div className="text-moon-600">{subtitle}</div>
              )}
            </div>
            <div className="flex">
              {rows.map((props, col) => {
                if (props == null) {
                  // Have empty space if row isn't filled
                  return <div className={METER_STYLE} key={col} />;
                }
                if (hasError) {
                  return (
                    <MeterSectionError key={col} className={METER_STYLE} />
                  );
                }
                if (loading) {
                  return (
                    <MeterSectionLoading
                      key={col}
                      title={props.title}
                      className={METER_STYLE}
                    />
                  );
                }
                return (
                  <MeterSection
                    key={col}
                    {...props}
                    isMonthly={isProMonthly}
                    isProPlan={isProPlan}
                    className={METER_STYLE}
                    button={
                      onClickCustomizePlan &&
                      isProPlan &&
                      (props.usageType === UsageType.UserSeats ||
                        props.usageType === UsageType.Weave) ? (
                        <Button
                          variant="secondary"
                          onClick={() => {
                            onClickCustomizePlan(true);
                          }}>
                          {props.usageType === UsageType.UserSeats
                            ? 'Add Models seats'
                            : 'Add Data Ingestion'}
                        </Button>
                      ) : (
                        props.button
                      )
                    }
                    secondaryButton={
                      onClickViewUsage != null && organization != null ? (
                        <Button
                          data-test={`view-usage-${props.usageType}`}
                          variant="ghost"
                          onClick={
                            props.usageType === UsageType.UserSeats
                              ? () =>
                                  history.push(
                                    accountSettingsMembersTab(organization.name)
                                  )
                              : () => onClickViewUsage(props.usageType)
                          }>
                          {props.usageType === UsageType.UserSeats
                            ? 'Manage users'
                            : 'View usage'}
                        </Button>
                      ) : null
                    }
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </Tailwind>
  );
};

export const CurrentBillingPeriodUsageSection = () => {
  const history = useHistory();
  const {selectedAccount} = useContext(AccountSelectorContext);
  const [showCustomizeModal, setShowCustomizeModal] = useState<boolean>(false);
  const [showNewStorageCheckoutModal, setShowNewStorageCheckoutModal] =
    useState(false);
  const {meterRows, organization, isProPlan, loading, hasError, privileges} =
    useGetAllUsageThisBillingPeriod(setShowNewStorageCheckoutModal);
  const primarySub = getPrimarySub(organization);
  return (
    <Tailwind>
      <CurrentBillingPeriodUsageSectionInner
        meterRows={meterRows}
        isProPlan={isProPlan}
        organization={organization}
        onClickCustomizePlan={setShowCustomizeModal}
        loading={loading}
        hasError={hasError}
        onClickViewUsage={
          selectedAccount?.name != null
            ? (usageType: UsageType) =>
                history.push(
                  historicUsageTab(
                    selectedAccount?.name,
                    TABS[usageType]?.urlPart ?? ''
                  )
                )
            : null
        }
      />
      {organization != null && isProPlan && (
        <StripeElementsComp>
          <CustomizePlanModal
            isOpen={showCustomizeModal}
            onClose={() => setShowCustomizeModal(false)}
            org={organization}
            onTransactionCompleted={() => {
              setShowCustomizeModal(false);
              window.location.reload();
            }}
          />
        </StripeElementsComp>
      )}
      {showNewStorageCheckoutModal && privileges && (
        <StripeElementsComp>
          <NewStorageCheckoutModal
            onTransactionCompleted={() => {
              setShowNewStorageCheckoutModal(false);
              window.location.reload();
            }}
            onClose={() => setShowNewStorageCheckoutModal(false)}
            orgID={organization?.id ?? ''}
            freeStorageInGB={freePersonalStorageInGB}
            primarySub={primarySub as SubscriptionForOrg<HasSubsWithSubType>}
            privileges={privileges!}
            isBuyingInference={true}
          />
        </StripeElementsComp>
      )}
    </Tailwind>
  );
};
